import { useState } from "react";
import { RotateCcw } from "lucide-react";
import {
  Alert,
  Button,
  Checkbox,
  FormModal,
  Input,
  ResponsiveGrid,
  Select,
  Switch,
  Text,
  Textarea,
} from "@marekl/adminer/components";
import { pluralizeCzech } from "@marekl/adminer/format";
import {
  floorLabel,
  useUpdateDisciplineCategories,
  type DeductionTable,
  type Discipline,
  type DisciplineCategory,
  type Floor,
  type Recording,
  type ScoringSheet,
} from "../../../lib/queries/rules";
import {
  RESULT_FORMULA_LABELS,
  formatSeconds,
  formatTimeRange,
  parseTimeInput,
} from "../../../lib/rulesLabels";
import {
  FIELD_LABELS,
  RESET,
  buildBulkPayload,
  categoryFormErrors,
  initialValues,
  isNoOp,
  type CategoryField,
  type CategoryFormValues,
} from "./categoryForm";

/**
 * Editor for one combination or a whole row / column of them.
 *
 * Every field is its own input, pre-filled only when this combination
 * overrides it; otherwise the input is empty and shows the inherited
 * value as its placeholder. So typing is the override, clearing is the
 * reset, and leaving a field alone changes nothing — no mode to pick
 * before you can start editing.
 */
export function CategoryEditModal({
  open,
  discipline,
  targets,
  deductionTables,
  scoringSheets,
  floors,
  recordings,
  onClose,
}: {
  open: boolean;
  discipline: Discipline;
  targets: DisciplineCategory[];
  deductionTables: DeductionTable[];
  scoringSheets: ScoringSheet[];
  floors: Floor[];
  recordings: Recording[];
  onClose: () => void;
}) {
  const single = targets.length === 1 ? targets[0] : undefined;
  const [initial] = useState<CategoryFormValues>(() => initialValues(single));
  const [values, setValues] = useState<CategoryFormValues>(initial);
  const save = useUpdateDisciplineCategories(discipline.id);

  const set = (field: CategoryField, value: string) =>
    setValues((v) => ({ ...v, [field]: value }));

  const defaultFloor = floors.find((f) => f.id === discipline.default_floor_id);
  const defaultRecording = recordings.find((r) => r.id === discipline.default_recording_id);

  const errors = categoryFormErrors(values);
  const payload = buildBulkPayload(
    targets.map((t) => t.id),
    initial,
    values,
  );

  const close = () => {
    save.reset();
    onClose();
  };

  /**
   * „Výchozí: X" — the discipline's own value, named.
   *
   * It is the whole placeholder only when editing a SINGLE cell, where an
   * empty field really does inherit. In a bulk edit it survives as the label
   * of the RESET option and as the prefix of the armed placeholder; what an
   * empty field means there is „Beze změny", which is `placeholderFor`'s
   * business.
   */
  const fallback = (label: string) => `Výchozí: ${label}`;

  /**
   * What the field shows while empty — and it must differ between the two
   * empty states, or the control cannot be read at all.
   *
   * Empty means "leave every target as it is"; empty WITH the „Výchozí"
   * toggle armed means "put every target back on the discipline default".
   * Both clear the input, so before this the field looked identical either
   * way and the whole state lived in a tint on a button in the suffix.
   */
  const placeholderFor = (field: CategoryField, label: string) => {
    if (values[field] === RESET) return `Vrátí se na výchozí: ${label}`;
    // Editing ONE cell, empty really does mean "inherit the default".
    // Editing several, empty means "leave each target on whatever it has" —
    // `buildBulkPayload` skips the field entirely — so „Výchozí: X" alone
    // would promise the one thing that will not happen. The Select below
    // says „Beze změny" for the same state; these two must not disagree.
    //
    // Both facts, though: the admin deciding whether to press „Výchozí"
    // needs to see what it would reset TO, and saying only „Beze změny"
    // hid the value until after they had committed to the act they were
    // trying to evaluate.
    if (!single) return `Beze změny · výchozí ${label}`;
    return fallback(label);
  };

  /**
   * The leading options of an override <Select>.
   *
   * Editing one cell needs two states — inherit or set — and empty is
   * inherit. Editing many needs three, because "leave each target as it
   * is" and "put them all back on the default" are different intentions
   * and empty already means the first. Hence the explicit RESET option:
   * without it the „Výchozí" choice equalled the initial value, changed
   * nothing, and a whole column could never be put back.
   */
  const leadingOptions = (label: string) =>
    single ? (
      <option value="">{fallback(label)}</option>
    ) : (
      <>
        <option value="">Beze změny</option>
        <option value={RESET}>{fallback(label)}</option>
      </>
    );

  /**
   * The same third state for a text field, which has no option list to
   * put it in. Absent when editing one cell — there, emptying the input
   * already is the reset.
   */
  const resetToggle = (field: CategoryField) =>
    single ? undefined : (
      <Button
        size="sm"
        // Outline at rest, so `pressed`'s filled face reads as a second state
        // rather than as a slightly different button. The icon says which act
        // it is; the label alone read as „set this to the default now", when
        // what it does is arm a reset that happens on save.
        weight="outline"
        startIcon={<RotateCcw size={14} aria-hidden="true" />}
        // One prop for the state and the face, so they cannot disagree.
        // This used to be `variant="primary"` plus a hand-written
        // `aria-pressed`, i.e. two things to keep in step.
        pressed={values[field] === RESET}
        // The label is „Výchozí" on all four of these, so without a name of
        // its own a screen reader announces four identical buttons and the
        // field they belong to lives only in `title` — a description, not a
        // name, and announced inconsistently.
        //
        // Named with a colon rather than a sentence: FIELD_LABELS holds
        // nominative noun phrases („Plusových porotců"), and dropping one into
        // „Vrátit … na výchozí" puts it in an accusative slot it was never
        // written for. A frame that does not inflect the label cannot get
        // its case wrong.
        aria-label={`Výchozí hodnota disciplíny: ${FIELD_LABELS[field]}`}
        title={
          values[field] === RESET
            ? "Zrušit vrácení na výchozí hodnotu disciplíny"
            : "Vrátit na výchozí hodnotu disciplíny"
        }
        onClick={() => set(field, values[field] === RESET ? "" : RESET)}
      >
        Výchozí
      </Button>
    );

  /** RESET is a sentinel, never something to show in the input itself. */
  const shown = (field: CategoryField) => (values[field] === RESET ? "" : values[field]);

  const noMinimum = values.time_min_seconds === "0:00";

  return (
    <FormModal
      open={open}
      title={single ? "Upravit kombinaci" : `Hromadná úprava (${targets.length} ${pluralizeCzech(targets.length, ["kombinace", "kombinace", "kombinací"])})`}
      submitLabel="Uložit"
      busy={save.isPending}
      canSubmit={Object.keys(errors).length === 0 && !isNoOp(payload)}
      error={save.error}
      onSubmit={() => save.mutate(payload, { onSuccess: close })}
      onClose={close}
      width="lg"
    >
      <Alert variant="info">
        {single
          ? "Prázdné pole znamená, že se použije výchozí hodnota disciplíny."
          : "Změní se jen pole, do kterých zasáhnete; prázdné pole necháte beze změny. Tlačítkem „Výchozí“ (u výběrů stejnojmennou volbou) vrátíte pole na výchozí hodnotu disciplíny ve všech vybraných kombinacích."}
      </Alert>

      {single ? (
        <Switch
          label={values.enabled === "on" ? "Povoleno: kombinace se soutěží" : "Zakázáno"}
          checked={values.enabled === "on"}
          onChange={(e) => set("enabled", e.target.checked ? "on" : "off")}
        />
      ) : (
        <Select
          label={FIELD_LABELS.enabled}
          value={values.enabled}
          onChange={(e) => set("enabled", e.target.value)}
        >
          <option value="">Beze změny</option>
          <option value="on">Povoleno: kombinace se soutěží</option>
          <option value="off">Zakázáno</option>
        </Select>
      )}

      <ResponsiveGrid columns={2}>
        <Input
          label={FIELD_LABELS.time_min_seconds}
          placeholder={placeholderFor(
            "time_min_seconds",
            discipline.default_time_min_seconds === undefined
              ? "nenastaveno"
              : formatSeconds(discipline.default_time_min_seconds),
          )}
          value={shown("time_min_seconds")}
          error={errors.time_min_seconds}
          suffix={resetToggle("time_min_seconds")}
          disabled={noMinimum || values.time_min_seconds === RESET}
          onChange={(e) => set("time_min_seconds", e.target.value)}
        />
        <Input
          label={FIELD_LABELS.time_max_seconds}
          placeholder={placeholderFor(
            "time_max_seconds",
            discipline.default_time_max_seconds === undefined
              ? "nenastaveno"
              : formatSeconds(discipline.default_time_max_seconds),
          )}
          value={shown("time_max_seconds")}
          error={errors.time_max_seconds}
          suffix={resetToggle("time_max_seconds")}
          disabled={values.time_max_seconds === RESET}
          onChange={(e) => set("time_max_seconds", e.target.value)}
        />
      </ResponsiveGrid>
      <Checkbox
        label="Bez minima"
        checked={noMinimum}
        onChange={(e) => set("time_min_seconds", e.target.checked ? "0:00" : "")}
        hint="Uloží se jako 0:00; soutěží se jen s horní hranicí."
      />

      <ResponsiveGrid columns={2}>
        <Select
          label={FIELD_LABELS.floor_id}
          value={values.floor_id}
          onChange={(e) => set("floor_id", e.target.value)}
        >
          {leadingOptions(defaultFloor ? floorLabel(defaultFloor) : "nenastaveno")}
          {floors.map((f) => (
            <option key={f.id} value={f.id}>
              {floorLabel(f)}
            </option>
          ))}
        </Select>
        {discipline.music_source === "federation" && (
          <Select
            label={FIELD_LABELS.recording_id}
            value={values.recording_id}
            onChange={(e) => set("recording_id", e.target.value)}
          >
            {leadingOptions(defaultRecording?.name ?? "nenastaveno")}
            {recordings.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </Select>
        )}
        <Select
          label={FIELD_LABELS.result_formula}
          value={values.result_formula}
          onChange={(e) => set("result_formula", e.target.value)}
        >
          {leadingOptions(RESULT_FORMULA_LABELS[discipline.default_result_formula])}
          {Object.entries(RESULT_FORMULA_LABELS).map(([v, label]) => (
            <option key={v} value={v}>
              {label}
            </option>
          ))}
        </Select>
      </ResponsiveGrid>

      <ResponsiveGrid columns={2}>
        <Input
          label={FIELD_LABELS.plus_judges}
          type="number"
          min={0}
          placeholder={placeholderFor("plus_judges", String(discipline.default_plus_judges))}
          value={shown("plus_judges")}
          error={errors.plus_judges}
          suffix={resetToggle("plus_judges")}
          disabled={values.plus_judges === RESET}
          onChange={(e) => set("plus_judges", e.target.value)}
        />
        <Input
          label={FIELD_LABELS.tech_judges}
          type="number"
          min={0}
          placeholder={placeholderFor("tech_judges", String(discipline.default_tech_judges))}
          value={shown("tech_judges")}
          error={errors.tech_judges}
          suffix={resetToggle("tech_judges")}
          disabled={values.tech_judges === RESET}
          onChange={(e) => set("tech_judges", e.target.value)}
        />
      </ResponsiveGrid>

      <ResponsiveGrid columns={2}>
        <Select
          label={FIELD_LABELS.deduction_table_id}
          value={values.deduction_table_id}
          onChange={(e) => set("deduction_table_id", e.target.value)}
        >
          {leadingOptions(
            deductionTables.find((t) => t.id === discipline.default_deduction_table_id)?.name ??
              "nenastaveno",
          )}
          {deductionTables.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </Select>
        <Select
          label={FIELD_LABELS.scoring_sheet_id}
          value={values.scoring_sheet_id}
          onChange={(e) => set("scoring_sheet_id", e.target.value)}
        >
          {leadingOptions(
            scoringSheets.find((s) => s.id === discipline.default_scoring_sheet_id)?.name ??
              "nenastaveno",
          )}
          {scoringSheets.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      </ResponsiveGrid>

      <Textarea
        label={FIELD_LABELS.note}
        rows={2}
        value={values.note}
        onChange={(e) => set("note", e.target.value)}
        hint="Poznámka k této kombinaci, například odkaz na výjimku v pravidlech."
      />

      {single && (
        <Text variant="muted">
          Výsledný časový limit:{" "}
          {formatTimeRange(
            values.time_min_seconds === ""
              ? (discipline.default_time_min_seconds ?? null)
              : parseTimeInput(values.time_min_seconds),
            values.time_max_seconds === ""
              ? (discipline.default_time_max_seconds ?? null)
              : parseTimeInput(values.time_max_seconds),
          )}
        </Text>
      )}
    </FormModal>
  );
}
